import React, { Children, isValidElement, ReactElement, useState } from 'react';
import styles from './style.module.scss';
import { testIds } from 'constants/test-constants';
import { buildClassName } from 'utils/build-class-name';
import { useThemeContext } from 'contexts/theme';
import { TabProps } from './tab';
import { PropsWithTestId } from 'types';
import { isExpTenant } from '@tenants';

type TabsProps = PropsWithTestId<{
  testId?: string;
  className?: string;
  children: ReactElement<TabProps>[];
  onTabChanged?: (tabId: string) => void;
}>;

function TabLabel({ id, label, isActive, handleTabClick }: { id: string; label: string; isActive: boolean; handleTabClick: (tabId: string) => void }) {
  const className = buildClassName(isActive ? styles.active : '');
  const handleClick = () => handleTabClick(id);
  return (
    <li className={className} onClick={handleClick} data-testid={`${id}-${testIds.tabLink}`}>
      {label}
    </li>
  );
}

export default function Tabs({ testId, children, className, onTabChanged }: TabsProps) {
  const { tenantId } = useThemeContext();
  const expTabStyles = isExpTenant(tenantId) ? styles.exptabs : '';
  const containerClassName = buildClassName(styles.component, className, expTabStyles);

  // Filter children to only include valid ReactElements
  const validChildren = Children.toArray(children).filter(
    (child): child is ReactElement<TabProps> =>
      isValidElement(child) && child.props?.id && child.props?.label
  );

  const tabIds = validChildren.map(tab => tab.props.id);
  const [activeTabId, setActiveTabId] = useState(tabIds.length ? tabIds[0] : '');
  const handleTabClick = (tabId: string) => {
    setActiveTabId(tabId);
    onTabChanged?.(tabId);
  };

  return (
    <div className={containerClassName} data-testid={testId}>
      <ul className={styles.tabs}>
        {validChildren.map((tab, index) => {
          const { id, label } = tab.props;
          return <TabLabel key={index} id={id} label={label} isActive={id === activeTabId} handleTabClick={handleTabClick} />;
        })}
      </ul>
      {validChildren.map((tab, index) => (
        <div key={`${tab.props.id}-${index}`} style={{ display: tab.props.id === activeTabId ? 'block' : 'none' }}>
          {tab}
        </div>
      ))}
    </div>
  );
}
